import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts"
import { formatearNumero } from "helpers/formatters"

// Componentes PRESENTACIONALES del historial de importaciones: reciben datos ya
// categorizados ({ etiqueta, cantidad, pct }) y solo los dibujan. Nunca leen
// campos de filas ni texto libre.

export const MAX_CATEGORIAS_VISIBLES = 8
export const MAX_LARGO_ETIQUETA = 40
export const ETIQUETA_OTROS_MOTIVOS = "Otros motivos"
export const ETIQUETA_OTROS = "Otros"
export const TEXTO_SIN_DATOS = "Sin datos para mostrar"

const COLOR_CREADOS = "#28a745"
const COLOR_SIN_DATOS = "#ffc107"
const COLOR_OTROS = "#adb5bd"
const COLORES_MOTIVOS = [
  "#dc3545",
  "#fd7e14",
  "#17a2b8",
  "#6f42c1",
  "#007bff",
  "#e83e8c",
  "#6c757d",
]
const PCT_MINIMO_ETIQUETA = 0.08
const RADIANES = Math.PI / 180

const ESTILO_OCULTO = {
  position: "absolute",
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
}

const MAYUSCULAS = /[A-ZÁÉÍÓÚÑÜ]{2,}/g
const COMILLAS = /["'“”‘’«»`]/

// Defensa en profundidad: una etiqueta que parece dato personal o texto crudo
// (comillas, demasiado larga, "APELLIDO, NOMBRE") no se muestra jamas.
export function etiquetaSegura(etiqueta) {
  if (typeof etiqueta !== "string") return false
  const t = etiqueta.trim()
  if (!t || t.length > MAX_LARGO_ETIQUETA) return false
  if (COMILLAS.test(t)) return false
  if (t.includes(",") && (t.match(MAYUSCULAS) || []).length >= 2) return false
  return true
}

const numero = v =>
  Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : 0

// Normaliza segmentos: etiquetas inseguras o repetidas de "otros" se suman en
// un unico segmento "otros", y nunca hay mas de MAX_CATEGORIAS_VISIBLES.
// Los segmentos con `fijo` (Creados, Sincronizados...) no se tocan.
export function prepararSegmentos(segmentos, etiquetaOtros, total) {
  const visibles = []
  let otros = 0
  segmentos.forEach(s => {
    const cantidad = numero(s.cantidad)
    if (cantidad === 0) return
    const esOtros = !s.fijo && s.etiqueta && s.etiqueta.trim() === etiquetaOtros
    if (!s.fijo && (!etiquetaSegura(s.etiqueta) || esOtros)) {
      otros += cantidad
    } else {
      visibles.push({ ...s, etiqueta: s.etiqueta.trim(), cantidad })
    }
  })
  const maxSinOtros = MAX_CATEGORIAS_VISIBLES - 1
  while (visibles.length > maxSinOtros) {
    // Se absorbe el ultimo segmento NO fijo (los fijos son lo importante).
    let idx = -1
    for (let i = visibles.length - 1; i >= 0; i--) {
      if (!visibles[i].fijo) {
        idx = i
        break
      }
    }
    if (idx < 0) break
    otros += visibles[idx].cantidad
    visibles.splice(idx, 1)
  }
  const sumaVisibles = visibles.reduce((a, s) => a + s.cantidad, 0)
  // Lo que no llego discriminado (total mayor a la suma) tambien es "otros".
  if (total > sumaVisibles + otros) otros = total - sumaVisibles
  if (otros > 0) {
    visibles.push({
      etiqueta: etiquetaOtros,
      cantidad: otros,
      color: COLOR_OTROS,
    })
  }
  const base = total > 0 ? total : visibles.reduce((a, s) => a + s.cantidad, 0)
  let i = 0
  return visibles.map(s => ({
    ...s,
    pct: base > 0 ? (s.cantidad / base) * 100 : 0,
    color: s.color || COLORES_MOTIVOS[i++ % COLORES_MOTIVOS.length],
  }))
}

const etiquetaRebanada = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  value,
}) => {
  if (percent < PCT_MINIMO_ETIQUETA) return null
  const radio = innerRadius + (outerRadius - innerRadius) * 0.55
  return (
    <text
      x={cx + radio * Math.cos(-midAngle * RADIANES)}
      y={cy + radio * Math.sin(-midAngle * RADIANES)}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={600}
      fill="#fff"
      style={{ textShadow: "0 0 3px rgba(0,0,0,.65)" }}
    >
      {formatearNumero(value, 0)}
    </text>
  )
}

const formatoPct = pct => `${formatearNumero(pct, 1)} %`

function GraficoTorta({ titulo, subtitulo, segmentos, testId }) {
  if (!segmentos.length) {
    return (
      <div data-testid={testId}>
        {titulo && (
          <p className="text-center text-muted mb-1" style={{ fontSize: 13 }}>
            {titulo}
          </p>
        )}
        <p className="text-center text-muted small mb-0">{TEXTO_SIN_DATOS}</p>
      </div>
    )
  }
  const datos = segmentos.map(s => ({
    name: s.etiqueta,
    value: s.cantidad,
    pct: s.pct,
    color: s.color,
  }))
  const resumen = `${titulo || "Gráfico"}${subtitulo ? `. ${subtitulo}` : ""}: ${segmentos
    .map(
      s =>
        `${s.etiqueta} ${formatearNumero(s.cantidad, 0)} (${formatoPct(s.pct)})`,
    )
    .join("; ")}`
  return (
    <div data-testid={testId} style={{ position: "relative" }}>
      {titulo && (
        <p className="text-center text-muted mb-0" style={{ fontSize: 13 }}>
          {titulo}
        </p>
      )}
      {subtitulo && (
        <p
          className="text-center mb-1"
          style={{ fontSize: 13, fontWeight: 600 }}
          data-testid={`${testId}-subtitulo`}
        >
          {subtitulo}
        </p>
      )}
      <div role="img" aria-label={resumen}>
        <div style={{ height: 200, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={datos}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={etiquetaRebanada}
                labelLine={false}
                isAnimationActive={false}
              >
                {datos.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v, n, p) => [
                  `${formatearNumero(v, 0)} (${formatoPct(p.payload.pct)})`,
                  n,
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul
          className="list-unstyled small mb-0 mt-1"
          data-testid={`${testId}-leyenda`}
        >
          {segmentos.map((s, i) => (
            <li
              key={i}
              className="d-flex align-items-baseline"
              style={{ gap: 6, minWidth: 0 }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: s.color,
                  flex: "0 0 10px",
                }}
              />
              <span
                style={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  overflowWrap: "anywhere",
                }}
              >
                {s.etiqueta}
              </span>
              <span className="text-nowrap">
                {formatearNumero(s.cantidad, 0)} · {formatoPct(s.pct)}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <table style={ESTILO_OCULTO} data-testid={`${testId}-tabla`}>
        <caption>{titulo || "Detalle"}</caption>
        <thead>
          <tr>
            <th>Categoría</th>
            <th>Cantidad</th>
            <th>Porcentaje</th>
          </tr>
        </thead>
        <tbody>
          {segmentos.map((s, i) => (
            <tr key={i}>
              <td>{s.etiqueta}</td>
              <td>{formatearNumero(s.cantidad, 0)}</td>
              <td>{formatoPct(s.pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const propsMotivos = PropTypes.arrayOf(
  PropTypes.shape({
    clave: PropTypes.string,
    etiqueta: PropTypes.string,
    cantidad: PropTypes.number,
    pct: PropTypes.number,
    fijo: PropTypes.bool,
    color: PropTypes.string,
  }),
)

const subtituloTotal = total => `Total procesado: ${formatearNumero(total, 0)}`

// `fijo` + `color` (opcionales) permiten fijar el color de categorias que no son
// un motivo de fallo (Creados, Actualizados...); sin ellos se usa la paleta.
const motivosASegmentos = motivos =>
  (motivos || []).map(m => ({
    etiqueta: m.etiqueta,
    cantidad: m.cantidad,
    ...(m.fijo && m.color ? { fijo: true, color: m.color } : {}),
  }))

const sumaCantidades = lista =>
  lista.reduce((a, m) => a + numero(m.cantidad), 0)

export function GraficoProcesadosPorMotivo({
  titulo,
  total,
  creados,
  noImportados,
  motivos,
  etiquetaCreados = "Creados",
  testId = "grafico-procesados",
}) {
  const { items, t } = useMemo(() => {
    const crea = numero(creados)
    const lista = motivosASegmentos(motivos)
    const totalCalc =
      numero(total) ||
      crea + Math.max(numero(noImportados), sumaCantidades(lista))
    const base = [
      ...(crea > 0
        ? [
            {
              etiqueta: etiquetaCreados,
              cantidad: crea,
              color: COLOR_CREADOS,
              fijo: true,
            },
          ]
        : []),
      ...lista,
    ]
    return {
      items: prepararSegmentos(base, ETIQUETA_OTROS_MOTIVOS, totalCalc),
      t: totalCalc,
    }
  }, [total, creados, noImportados, motivos, etiquetaCreados])
  return (
    <GraficoTorta
      titulo={titulo}
      subtitulo={t > 0 ? subtituloTotal(t) : null}
      segmentos={items}
      testId={testId}
    />
  )
}

export function GraficoArca({
  titulo,
  total,
  sincronizados,
  sinDatos,
  motivos,
}) {
  const { items, t } = useMemo(() => {
    const sinc = numero(sincronizados)
    const sin = numero(sinDatos)
    const lista = motivosASegmentos(motivos)
    const totalCalc = numero(total) || sinc + sin + sumaCantidades(lista)
    const base = [
      ...(sinc > 0
        ? [
            {
              etiqueta: "Sincronizados",
              cantidad: sinc,
              color: COLOR_CREADOS,
              fijo: true,
            },
          ]
        : []),
      ...(sin > 0
        ? [
            {
              etiqueta: "Sin datos",
              cantidad: sin,
              color: COLOR_SIN_DATOS,
              fijo: true,
            },
          ]
        : []),
      ...lista,
    ]
    return {
      items: prepararSegmentos(base, ETIQUETA_OTROS_MOTIVOS, totalCalc),
      t: totalCalc,
    }
  }, [total, sincronizados, sinDatos, motivos])
  return (
    <GraficoTorta
      titulo={titulo}
      subtitulo={t > 0 ? subtituloTotal(t) : null}
      segmentos={items}
      testId="grafico-arca"
    />
  )
}

export function GraficoProductores({ titulo, categorias }) {
  const { items, t } = useMemo(() => {
    const lista = motivosASegmentos(categorias)
    const total = sumaCantidades(lista)
    return { items: prepararSegmentos(lista, ETIQUETA_OTROS, total), t: total }
  }, [categorias])
  return (
    <GraficoTorta
      titulo={titulo}
      subtitulo={t > 0 ? subtituloTotal(t) : null}
      segmentos={items}
      testId="grafico-productores"
    />
  )
}

GraficoProcesadosPorMotivo.propTypes = {
  titulo: PropTypes.string,
  total: PropTypes.number,
  creados: PropTypes.number,
  noImportados: PropTypes.number,
  motivos: propsMotivos,
  etiquetaCreados: PropTypes.string,
  testId: PropTypes.string,
}
GraficoArca.propTypes = {
  titulo: PropTypes.string,
  total: PropTypes.number,
  sincronizados: PropTypes.number,
  sinDatos: PropTypes.number,
  motivos: propsMotivos,
}
GraficoProductores.propTypes = {
  titulo: PropTypes.string,
  categorias: propsMotivos,
}
GraficoTorta.propTypes = {
  titulo: PropTypes.string,
  subtitulo: PropTypes.string,
  segmentos: PropTypes.array.isRequired,
  testId: PropTypes.string.isRequired,
}
